import { type ClassValue, clsx } from "clsx";
import { twMerge } from "tailwind-merge";
import { TAG_CATEGORIES, type TagCategory } from "../types";
import i18n from "./i18n";

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

export function formatDate(date: Date | string | null | undefined): string {
  // null/undefinedチェック
  if (!date) {
    return i18n.t("date.unknown");
  }

  const d = typeof date === "string" ? new Date(date) : date;

  // 無効な日付チェック
  if (!d || Number.isNaN(d.getTime())) {
    return i18n.t("date.unknown");
  }

  // 現在の言語に応じたロケールを使用
  const locale = i18n.language === "ja" ? "ja-JP" : "en-US";
  return new Intl.DateTimeFormat(locale, {
    year: "numeric",
    month: "long",
    day: "numeric",
  }).format(d);
}

export function formatYearMonth(date: Date | string | null | undefined): string {
  // null/undefinedチェック
  if (!date) {
    return i18n.t("date.unknown");
  }

  const d = typeof date === "string" ? new Date(date) : date;

  // 無効な日付チェック
  if (!d || Number.isNaN(d.getTime())) {
    return i18n.t("date.unknown");
  }

  const year = d.getFullYear();
  const month = String(d.getMonth() + 1).padStart(2, "0");
  return `${year}-${month}`;
}

export function formatRelativeTime(date: Date | string | null | undefined): string {
  // null/undefinedチェック
  if (!date) {
    return i18n.t("date.unknown");
  }

  const d = typeof date === "string" ? new Date(date) : date;

  // 無効な日付チェック
  if (!d || Number.isNaN(d.getTime())) {
    return i18n.t("date.unknown");
  }

  const now = new Date();
  const diffInMs = now.getTime() - d.getTime();
  const diffInHours = diffInMs / (1000 * 60 * 60);
  const diffInDays = diffInMs / (1000 * 60 * 60 * 24);

  if (diffInHours < 1) {
    return i18n.t("date.lessThanHour");
  } else if (diffInHours < 24) {
    return i18n.t("date.hoursAgo", { count: Math.floor(diffInHours) });
  } else if (diffInDays < 7) {
    return i18n.t("date.daysAgo", { count: Math.floor(diffInDays) });
  } else {
    return formatDate(d);
  }
}

export function truncateText(text: string, maxLength: number): string {
  if (text.length <= maxLength) return text;
  return `${text.substring(0, maxLength).trim()}...`;
}

/**
 * タグ名を現在の言語に応じて取得するヘルパー関数
 * @param tag - タグオブジェクト
 * @param currentLanguage - 現在の言語設定（'en' | 'ja'）
 * @returns 現在の言語に対応するタグ名
 */
export function getTagName(
  tag: { name: string; nameEn?: string | null },
  currentLanguage: string,
): string {
  // 英語の場合、nameEnがあればそれを返し、なければnameにフォールバック
  if (currentLanguage === "en" && tag.nameEn) {
    return tag.nameEn;
  }
  // 日本語の場合、または英語名がない場合はnameを返す
  return tag.name;
}

/**
 * タグのファセット分類を取得するヘルパー関数
 * @param tag - タグオブジェクト
 * @returns ファセット分類。category が未設定・未知の値の場合は "function" にフォールバック
 */
export function getTagCategory(tag: { category?: string | null }): TagCategory {
  return (TAG_CATEGORIES as readonly string[]).includes(tag.category ?? "")
    ? (tag.category as TagCategory)
    : "function";
}

/**
 * 国コードから国名を取得（i18n対応）
 * @param countryCode 国コード（例: "US", "JP"）
 * @returns 現在の言語に対応する国名、または null
 */
export function getCountryName(countryCode: string | null | undefined): string | null {
  if (!countryCode) return null;

  // i18n の翻訳データから国名を取得
  const countryKey = `countries.${countryCode}`;
  const countryName = i18n.t(countryKey, { defaultValue: null });

  // 翻訳が見つからない場合は null を返す（キーそのものが返ってくる場合も null）
  return countryName === countryKey ? null : countryName;
}

/**
 * prefers-reduced-motion: ユーザーが動きの抑制を希望しているか
 */
export function prefersReducedMotion(): boolean {
  if (typeof window === "undefined") return false;
  return window.matchMedia("(prefers-reduced-motion: reduce)").matches;
}

/**
 * OS検出: ユーザーのOSを判定
 */
export function detectOS(): "mac" | "windows" | "linux" {
  if (typeof window === "undefined") return "linux";

  const platform = window.navigator.platform.toLowerCase();
  const userAgent = window.navigator.userAgent.toLowerCase();

  if (platform.includes("mac") || userAgent.includes("mac")) {
    return "mac";
  }
  if (platform.includes("win") || userAgent.includes("win")) {
    return "windows";
  }
  return "linux";
}

/**
 * 検索ショートカットキー取得: OSに応じた表示用文字列を返す
 */
export function getSearchShortcutKey(): string {
  const os = detectOS();
  return os === "mac" ? "⌘K" : "Ctrl+K";
}

/**
 * 数値を読みやすい形式にフォーマット
 * - 1,000以上: "1K", "10K", "100K"
 * - 1,000,000以上: "1M", "10M"
 * - それ以下: そのまま表示
 *
 * @param count 数値
 * @returns フォーマット済み文字列
 */
export function formatCount(count: number): string {
  if (count >= 1000000) {
    return `${(count / 1000000).toFixed(1).replace(/\.0$/, "")}M`;
  }
  if (count >= 1000) {
    return `${(count / 1000).toFixed(1).replace(/\.0$/, "")}K`;
  }
  return count.toString();
}

/**
 * 管理画面用の日付・日時フォーマット
 * @param dateString ISO形式の日付文字列またはnull
 * @param options フォーマットオプション
 * @returns フォーマット済み日付文字列、またはハイフン（null時）
 */
export function formatAdminDate(
  dateString: string | null,
  options: { includeTime?: boolean; locale?: string } = {},
): string {
  if (!dateString) return "-";

  const { includeTime = true, locale = "ja-JP" } = options;

  const formatOptions: Intl.DateTimeFormatOptions = {
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  };

  if (includeTime) {
    formatOptions.hour = "2-digit";
    formatOptions.minute = "2-digit";
  }

  return new Date(dateString).toLocaleDateString(locale, formatOptions);
}
